import { z } from 'zod';
import {
  TaskType,
  taskTypeZodWithCatch,
} from 'packages/lib/api/annotations/contract/enums';

/** Allowed GroupType query param values for backend APIs */
export const groupTypeZod = z.enum([
  'requestID',
  'pipeline',
  'workflow',
  'sampleGroup',
  'taskGroup',
]);
export type GroupType = z.infer<typeof groupTypeZod>;

/**
 * One row of `GET /cost/group/{token}`, covering every task under that group that
 * survived the startDate/endDate, taskType and managerEmail filters.
 */
export const costGroupItemZod = z.object({
  token: z.string(),
  name: z.string(),
  taskType: taskTypeZodWithCatch,
  avgLabelingCostPerTask: z.number(),
  /** Null when the task type has no frame dimension. */
  avgLabelingCostPerFrame: z.number().nullable(),
  avgLabelingCostPerAnnotation: z.number().nullable(),
  summediMeritLabelingCost: z.number(),
  summediMeritQACost: z.number(),
  summediMeritAuditCost: z.number(),
  summedScaleCost: z.number(),
  /** Total number of tasks included in these averages and sums. */
  summedNumTasks: z.number(),
  /** Annotation count per task, summed for all included tasks. */
  summedNumAnnotations: z.number(),
  /** Frame count per task, summed for all included tasks. Omitted when N/A. */
  summedNumFrames: z.number().optional(),
});
export type CostGroupItem = z.infer<typeof costGroupItemZod>;

export const costGroupResponseZod = z.array(costGroupItemZod);

/** One point of `GET /cost/group/{token}/series`. */
export const costSeriesPointZod = z.object({
  /** YYYY-MM-DD */
  date: z.string(),
  labelingCost: z.number(),
  qaCost: z.number(),
  auditCost: z.number(),
});
export type CostSeriesPoint = z.infer<typeof costSeriesPointZod>;

export const costSeriesResponseZod = z.array(costSeriesPointZod);

/** Shared by both cost endpoints. */
export interface CostQuery {
  /** Token or request ID, matching `groupType`. */
  token: string;
  groupType: GroupType;
  /** YYYY-MM-DD */
  startDate: string;
  /** YYYY-MM-DD */
  endDate: string;
  /** Empty means all task types, in which case the param is omitted. */
  taskTypes: TaskType[];
  /** The signed-in user's email when the Contributors menu is on Direct Reports. */
  managerEmail?: string;
}
